import React, { Fragment, useEffect, useState } from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Button,
} from "@material-ui/core";
import { Pagination } from "@mui/material";
import { Helmet } from "react-helmet";
import { useDispatch } from "react-redux";
import { BookCard, PageBanner } from "../../components";
import BookKleverBookCard from "../../components/BookCard/BookKleverBookCard";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { backend } from "../../api";
import { Skeleton } from "@material-ui/lab";

function Shop() {
  const [books, setBooks] = useState([]);
  const [kleverBooks, setKleverBooks] = useState([]);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [view, setView] = useState("E-BOOK");
  const booksPerPage = 12;
  const dispatch = useDispatch();

  useEffect(() => {
    if (view === "KleverBook") {
      fetchKleverBooks();
    } else {
      fetchAllBooks();
    }
  }, [view]);

  const fetchAllBooks = () => {
    setIsLoading(true);
    backend
      .get("/book?isPublished=true&isActive=true&price&isAuthorAdmin=false")
      .then(({ data }) => {
        const val = data.data.filter((i) => i.pre_order === false);
        setBooks(val);
        fetchCategories(); // Fetch categories after books are fetched
      })
      .catch((err) => {
        setError(err.response ? err.response.data.message : err.message);
      });
  };

  const fetchKleverBooks = () => {
    setIsLoading(true);
    backend
      .get("/adverbook?isActive=true")
      .then(({ data }) => {
        const kleverBooks = data.data.filter(
          (book) =>
            book?.addToKlever === true &&
            book?.status[1]?.status === "approved" &&
            book?.isAuthorAdmin === false
        );
        setKleverBooks(kleverBooks);
        fetchCategories(); // Fetch categories after kleverBooks are fetched
      })
      .catch((err) => {
        setError(err.response ? err.response.data.message : err.message);
      });
  };

  const fetchCategories = () => {
    backend
      .get("/category")
      .then(({ data }) => {
        const activeCategories = data.data.filter((cat) => cat.isActive);
        setCategories(activeCategories);
      })
      .catch((err) => {
        setError(err.response ? err.response.data.message : err.message);
      })
      .finally(() => setIsLoading(false));
  };

  // Filter books based on selected categories
  const filteredBooks = (view === "E-BOOK" ? books : kleverBooks).filter(
    (book) => {
      const bookCategories = book.book_category.map((cat) => cat._id);
      return selectedCategories.length
        ? selectedCategories.some((selectedCat) =>
            bookCategories.includes(selectedCat)
          )
        : true;
    }
  );

  // Get current books based on active view
  const currentBooks = filteredBooks;
  const indexOfLastBook = currentPage * booksPerPage;
  const indexOfFirstBook = indexOfLastBook - booksPerPage;
  const currentDisplayBooks = currentBooks.slice(
    indexOfFirstBook,
    indexOfLastBook
  );

  const handleCategoryChange = (event) => {
    const { name, checked } = event.target;
    setSelectedCategories((prevSelected) =>
      checked ? [...prevSelected, name] : prevSelected.filter((catId) => catId !== name)
    );
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  return (
    <Fragment>
      <Helmet>
        <title>Shop - d2p</title>
      </Helmet>
      <ToastContainer />
      {/* <PageBanner title="Shop" image="/assets/images/bg4.jpg" /> */}
      <Container style={{ marginTop: "120px", marginBottom: "60px" }}>
        <Grid container spacing={6}>
          <Grid item xs={12} sm={4} md={3}>
            {isLoading ? (
              <Box mb={2} bgcolor="white" boxShadow={3} borderRadius={4} p={2}>
                <Skeleton animation="wave" height={40} width="80%" />
                <Skeleton animation="wave" height={40} />
                <Skeleton animation="wave" height={40} />
                <Skeleton animation="wave" height={40} />
              </Box>
            ) : (
              <Box mb={2} bgcolor="white" boxShadow={3} borderRadius={4} p={2}>
                <Typography variant="h6">Categories</Typography>
                <FormGroup>
                  {categories.map((category) => (
                    <FormControlLabel
                      key={category._id}
                      control={
                        <Checkbox
                          checked={selectedCategories.includes(category._id)}
                          onChange={handleCategoryChange}
                          name={category._id}
                        />
                      }
                      label={category.category_name}
                    />
                  ))}
                </FormGroup>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sm={8} md={9}>
            <Box display="flex" mb={2}>
              <Button
                variant={view === "E-BOOK" ? "contained" : "outlined"}
                onClick={() => setView("E-BOOK")}
                style={{
                  backgroundColor: view === "E-BOOK" ? "#FECE00" : "transparent",
                  color: view === "E-BOOK" ? "#000" : "#FECE00",
                  marginRight: "10px",
                }}
              >
                E-BOOK
              </Button>
              <Button
                variant={view === "KleverBook" ? "contained" : "outlined"}
                onClick={() => setView("KleverBook")}
                style={{
                  backgroundColor: view === "KleverBook" ? "#FECE00" : "transparent",
                  color: view === "KleverBook" ? "#000" : "#FECE00",
                }}
              >
                KleverBook
              </Button>
            </Box>
            {isLoading ? (
              <Grid container spacing={2}>
                {[...Array(12)].map((_, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Skeleton
                      animation="wave"
                      variant="rectangular"
                      height={250}
                      width="100%"
                      style={{ marginBottom: "16px" }}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : currentDisplayBooks.length > 0 ? (
              <Grid container spacing={2}>
                {currentDisplayBooks.map((book, index) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    key={index}
                    style={{ marginBottom: "16px" }}
                  >
                    {view === "E-BOOK" ? (
                      <BookCard
                        title={book.book_title}
                        price={book.price.digital_price}
                        cover={book.book_cover}
                        bookId={book._id}
                      />
                    ) : (
                      <BookKleverBookCard
                        title={book.book_title}
                        cover={book.book_cover}
                        bookId={book._id}
                      />
                    )}
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box textAlign="center" mt={4}>
                <Typography variant="subtitle2">
                  {selectedCategories.length > 0
                    ? "No books found for the selected categories."
                    : "No books available."}
                </Typography>
              </Box>
            )}
            {currentBooks.length > booksPerPage && (
              <Box display="flex" justifyContent="center" mt={4}>
                <Pagination
                  count={Math.ceil(currentBooks.length / booksPerPage)}
                  page={currentPage}
                  onChange={handlePageChange}
                  sx={{
                    "& .MuiPaginationItem-root": {
                      "&:hover": {
                        backgroundColor: "#FECE00",
                      },
                    },
                    "& .Mui-selected": {
                      backgroundColor: "#FECE00",
                      color: "#000",
                    },
                    "& .MuiPaginationItem-ellipsis": {
                      color: "#FECE00",
                    },
                  }}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>
    </Fragment>
  );
}

export default Shop;
